/**
 * WithVerticalSources Component
 * Wraps chat content and displays a vertical sources panel on the right side
 * Matches online-community's WithVerticalSources structure
 */

import React, { ReactNode } from 'react';

import { useTranscriptContext } from '../../../../store/transcript/utils/useTranscriptContext';
import { MessageReference } from '../../../Message/types/messages';
import { useOcFabWidgetOptions } from '../../contexts/OcFabWidgetOptionsContext';
import { useElementWidth } from '../../hooks/useElementWidth';
import SourceListVertical from './SourceListVertical';
import { VerticalSourcesContent, VerticalSourcesPanel } from './WithVerticalSources.styled';

// Minimum width required to show vertical sources (matches online-community's laptopMaxWidth)
const MIN_WIDTH_FOR_VERTICAL_SOURCES = 1024;

export interface WithVerticalSourcesProps {
  children: ReactNode;
  className?: string;
}

/**
 * Gets the most recent AI assistant message sources
 */
const useMostRecentAiSources = (): MessageReference[] => {
  const { state } = useTranscriptContext();
  
  if (!state.messages || state.messages.length === 0) {
    return [];
  }

  // Find the most recent assistant message with sources
  for (let i = state.messages.length - 1; i >= 0; i--) {
    const message = state.messages[i];
    if (message.role === 'assistant' && message.messageReferences && message.messageReferences.length > 0) {
      return message.messageReferences;
    }
  }

  return [];
};

/**
 * Gets the most recent AI response ID (for checking if we have AI responses)
 */
const useMostRecentAiResponseId = (): string => {
  const { state } = useTranscriptContext();
  
  if (!state.messages || state.messages.length === 0) {
    return '';
  }

  // Find the most recent assistant message
  for (let i = state.messages.length - 1; i >= 0; i--) {
    const message = state.messages[i];
    if (message.role === 'assistant' && message.messageId) {
      return message.messageId;
    }
  }

  return '';
};

/**
 * WithVerticalSources Component
 * Creates a split layout with chat on the left and sources panel on the right
 * Matches online-community pattern
 */
export const WithVerticalSources: React.FC<WithVerticalSourcesProps> = ({
  children,
  className = '',
}) => {
  const {showVerticalSources} = useOcFabWidgetOptions();
  const mostRecentSources = useMostRecentAiSources();
  const mostRecentAiResponseId = useMostRecentAiResponseId();
  const [ref, containerWidth] = useElementWidth();
  
  // Initial state is when we don't have AI responses yet (matches OC EMPTY_GUID check)
  const isInitialState = mostRecentAiResponseId === '';
  
  // Match OC logic: check if we have AI responses and sufficient width
  const shouldShowSources = 
    showVerticalSources &&
    mostRecentAiResponseId !== '' && 
    containerWidth >= MIN_WIDTH_FOR_VERTICAL_SOURCES;

  return (
    <VerticalSourcesPanel className={className} ref={ref}>
      <VerticalSourcesContent $isInitialState={isInitialState}>{children}</VerticalSourcesContent>
      {shouldShowSources && (
        <SourceListVertical sources={mostRecentSources} />
      )}
    </VerticalSourcesPanel>
  );
};

export default WithVerticalSources;

